/**
 * Resilience helpers for Supabase calls.
 *
 * `withRetry` retries on the auth-token lock-stolen race that the SDK
 * surfaces under multi-tab / realtime contention. Originally lived inside
 * RosterImport for the bulk roster upsert; lifted out so other admin
 * actions (toggleAdmin, etc.) can reuse it.
 *
 * `withTimeout` races a promise against a deadline so a hung websocket
 * can never silently freeze a UI handler. Pair with retry to get both
 * lock-recovery AND visible failure.
 */

const isLockStolen = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /\bLock\b.*\b(stolen|stole)\b/i.test(msg);
};

/** Retry `fn` up to `maxAttempts` times when the SDK throws the
 *  "Lock … was released because another request stole it" race.
 *  Other errors propagate immediately. Backoff: 200/500/1000/1500ms. */
export async function withRetry<T>(
  label: string,
  fn: () => Promise<T>,
  maxAttempts = 4,
): Promise<T> {
  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isLockStolen(err)) throw err;
      console.warn(
        `[${label}] auth-lock contention on attempt ${attempt}/${maxAttempts}, retrying…`,
      );
      const delay = [200, 500, 1000][attempt - 1] ?? 1500;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("Auth lock contention persisted after retries.");
}

/** Race `fn()` against a timer. If the promise doesn't settle within
 *  `timeoutMs`, throw a timeout error so the caller can surface it.
 *  Use this for any Supabase call that might hang on a stuck websocket. */
export async function withTimeout<T>(
  label: string,
  fn: () => Promise<T>,
  timeoutMs: number,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      fn(),
      new Promise<T>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} timed out after ${timeoutMs}ms`)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/** Direct REST call to PostgREST that bypasses the Supabase JS client
 *  ENTIRELY. Under realtime load the SDK's `getSession()` ALSO contests
 *  the same `navigator.locks` resource as outbound calls, so going via
 *  `sb.auth.getSession()` wasn't a true bypass — `getSession()` itself
 *  could hang for 5–30s and there was no way to abort it.
 *
 *  Reading the JWT straight out of localStorage skips that lock entirely.
 *  The session blob is what `persistSession: true` writes after every
 *  refresh, so it's authoritative as long as the user has signed in this
 *  tab and the access_token hasn't expired. We check expires_at before
 *  trusting it — if expired, ask the user to refresh (the SDK auto-
 *  refreshes in the background but we don't want to await that here).
 *
 *  AbortController gives a real network-level timeout on the fetch, not
 *  a JS-level race that the request could outlive. */
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../config";

interface CachedSession {
  access_token?: string;
  refresh_token?: string;
  expires_at?: number; // unix seconds (Supabase convention)
  expires_in?: number;
  token_type?: string;
  user?: unknown;
}

function projectRef(): string {
  return new URL(SUPABASE_URL).hostname.split(".")[0] ?? "";
}

function sessionStorageKey(): string {
  return `sb-${projectRef()}-auth-token`;
}

function readCachedSession(): CachedSession | null {
  try {
    const ref = projectRef();
    if (!ref) return null;
    const raw = localStorage.getItem(`sb-${ref}-auth-token`);
    if (!raw) return null;
    return JSON.parse(raw) as CachedSession;
  } catch {
    return null;
  }
}

/** Background refresh of the auth session via raw fetch, bypassing the
 *  SDK lock entirely. Triggered from authHeaders when we notice the
 *  cached access_token is within 30s of expiring (or already expired).
 *
 *  Writes the new token back to localStorage in the SDK's expected shape
 *  so subsequent restFetch calls (and the SDK's own background tasks)
 *  see the fresh credentials. If the refresh fails — bad refresh token,
 *  network out, server down — we silently swallow; the user will see
 *  "Session expired" on their next save attempt and can refresh the
 *  page manually as the recovery path of last resort.
 *
 *  Concurrent calls: gated by `refreshInFlight` so a flurry of saves
 *  with an expired token only fires one refresh request, not 50. */
let refreshInFlight: Promise<void> | null = null;
async function refreshSessionViaRest(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const session = readCachedSession();
      if (!session?.refresh_token) return;
      const resp = await fetch(
        `${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,
        {
          method: "POST",
          headers: {
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh_token: session.refresh_token }),
        },
      );
      if (!resp.ok) return;
      const fresh = (await resp.json()) as Partial<CachedSession>;
      if (!fresh.access_token) return;
      const merged: CachedSession = {
        ...session,
        access_token: fresh.access_token,
        refresh_token: fresh.refresh_token ?? session.refresh_token,
        expires_in: fresh.expires_in,
        expires_at: Math.floor(Date.now() / 1000) + (fresh.expires_in ?? 3600),
      };
      try {
        localStorage.setItem(sessionStorageKey(), JSON.stringify(merged));
      } catch {
        /* quota / private mode — refresh effectively lost, manual page reload needed */
      }
    } catch {
      /* network / parse — silent, user can refresh page to recover */
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Shared header / session prep for all REST helpers.
 *
 *  If the cached access_token is within 30s of expiring, fire a
 *  background refresh via raw fetch (bypassing the SDK lock) and throw
 *  a transient error. The 5–60s retry timer in pushOnce will pick up
 *  the refreshed token on its next attempt — the user usually doesn't
 *  notice. If the refresh itself fails (bad refresh_token, etc.) the
 *  user keeps seeing the "Session expired" toast every retry; manual
 *  page-refresh is the recovery path of last resort. */
function authHeaders(): Record<string, string> {
  const session = readCachedSession();
  if (!session?.access_token) {
    throw new Error("Not signed in — refresh the page and sign back in.");
  }
  const nowSec = Math.floor(Date.now() / 1000);
  if (session.expires_at && session.expires_at < nowSec + 30) {
    void refreshSessionViaRest();
    throw new Error(
      "Session expired — refreshing automatically, try again in a moment.",
    );
  }
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };
}

/** Run `fetch` with a real AbortController-backed timeout and decode any
 *  non-2xx into a useful error. Shared by every REST helper here so the
 *  error / timeout shape is consistent across PATCH, POST, DELETE. */
async function restFetch(
  url: string,
  init: Parameters<typeof fetch>[1],
  timeoutMs: number,
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { ...init, signal: controller.signal });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200) || resp.statusText}`);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Same as restFetch but parses + returns the JSON body. Used by
 *  restPatch to verify that the PATCH actually affected at least
 *  one row (via `Prefer: return=representation`). */
async function restFetchJson(
  url: string,
  init: Parameters<typeof fetch>[1],
  timeoutMs: number,
): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { ...init, signal: controller.signal });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200) || resp.statusText}`);
    }
    const text = await resp.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      // Server returned a non-JSON 2xx — treat as empty for downstream
      // length checks; the caller will surface "0 rows" appropriately.
      return null;
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** PATCH a row. Asks PostgREST to return the affected row in the response
 *  body via `Prefer: return=representation` so we can refuse to call it a
 *  success when zero rows actually changed.
 *
 *  Why: PostgREST returns HTTP 204 for a PATCH that matched no rows OR
 *  that was silently filtered by an RLS USING clause. Plain 2xx-means-OK
 *  logic would treat both cases as success, flip the optimistic UI to
 *  the new state, and leave the DB untouched — that's the bug that hid
 *  failed admin promotions during the test campaign. Returning the
 *  representation forces a non-empty body on real success, so we can
 *  detect the 0-rows case and throw a real error. */
export async function restPatch(
  table: string,
  filter: string,
  body: Record<string, unknown>,
  timeoutMs = 12000,
): Promise<void> {
  const json = await restFetchJson(
    `${SUPABASE_URL}/rest/v1/${table}?${filter}`,
    {
      method: "PATCH",
      headers: { ...authHeaders(), Prefer: "return=representation" },
      body: JSON.stringify(body),
    },
    timeoutMs,
  );
  // PostgREST returns the affected rows as an array. Empty array = the
  // filter matched nothing OR RLS silently filtered the row out before
  // the update could fire. Either way the user wanted a change to land
  // and it didn't — fail loudly so the caller can surface a toast.
  if (Array.isArray(json) && json.length === 0) {
    throw new Error(
      `Update to ${table} affected 0 rows — either the row doesn't exist or permissions denied the change.`,
    );
  }
}

/** POST with upsert semantics. Uses PostgREST's
 *  `Prefer: resolution=merge-duplicates` so an insert that hits the
 *  primary-key conflict updates the existing row instead of erroring. */
export async function restUpsert(
  table: string,
  rows: Array<Record<string, unknown>>,
  timeoutMs = 12000,
): Promise<void> {
  if (rows.length === 0) return;
  await restFetch(
    `${SUPABASE_URL}/rest/v1/${table}`,
    {
      method: "POST",
      headers: {
        ...authHeaders(),
        Prefer: "return=minimal,resolution=merge-duplicates",
      },
      body: JSON.stringify(rows),
    },
    timeoutMs,
  );
}

/** Call a Postgres function (PostgREST RPC). Used for operations that
 *  must run atomically server-side — e.g. equipment_create_booking,
 *  which re-validates availability with the item rows locked so two
 *  simultaneous submissions can't oversubscribe a unit. Same raw-fetch
 *  rationale as the helpers above (bypasses the SDK auth-token lock).
 *  PostgREST surfaces `raise exception` messages in the error body,
 *  which restFetch already extracts — so the caller's toast shows the
 *  server's human-readable reason. */
export async function restRpc<T>(
  fn: string,
  args: Record<string, unknown>,
  timeoutMs = 12000,
): Promise<T[]> {
  const json = await restFetchJson(
    `${SUPABASE_URL}/rest/v1/rpc/${fn}`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(args),
    },
    timeoutMs,
  );
  return Array.isArray(json) ? (json as T[]) : json === null ? [] : [json as T];
}

/** Invoke a Supabase Edge Function with the user's session. Used to
 *  nudge `equipment-notify` (drain the email outbox) right after a
 *  booking action — the Database Webhook does the same server-side, so
 *  this is belt-and-braces and its failure is never surfaced as an
 *  error to the user (claiming is atomic; nothing double-sends). */
export async function invokeFunction<T>(
  name: string,
  body: Record<string, unknown> = {},
  timeoutMs = 20000,
): Promise<T> {
  const json = await restFetchJson(
    `${SUPABASE_URL}/functions/v1/${name}`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    },
    timeoutMs,
  );
  return json as T;
}

/** Upload one file to a Supabase Storage bucket (raw fetch, same
 *  session handling as the REST helpers). `path` segments are encoded
 *  individually so slashes keep their meaning. No overwrite: a path
 *  clash is an error, callers generate unique names. */
export async function storageUpload(
  bucket: string,
  path: string,
  file: Blob,
  timeoutMs = 60000,
): Promise<void> {
  const enc = path.split("/").map(encodeURIComponent).join("/");
  await restFetch(
    `${SUPABASE_URL}/storage/v1/object/${bucket}/${enc}`,
    {
      method: "POST",
      headers: {
        ...authHeaders(),
        "Content-Type": file.type || "application/octet-stream",
        "x-upsert": "false",
      },
      body: file,
    },
    timeoutMs,
  );
}

/** Delete Storage objects by exact path (the "delete multiple" endpoint;
 *  RLS on storage.objects decides who may). */
export async function storageDelete(bucket: string, paths: string[], timeoutMs = 30000): Promise<void> {
  if (paths.length === 0) return;
  await restFetch(
    `${SUPABASE_URL}/storage/v1/object/${bucket}`,
    { method: "DELETE", headers: authHeaders(), body: JSON.stringify({ prefixes: paths }) },
    timeoutMs,
  );
}

/** Download a private Storage object as a Blob (RLS on storage.objects
 *  decides who may). */
export async function storageDownload(
  bucket: string,
  path: string,
  timeoutMs = 60000,
): Promise<Blob> {
  const enc = path.split("/").map(encodeURIComponent).join("/");
  const headers = authHeaders();
  delete headers["Content-Type"];
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(`${SUPABASE_URL}/storage/v1/object/authenticated/${bucket}/${enc}`, {
      headers,
      signal: controller.signal,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200) || resp.statusText}`);
    }
    return await resp.blob();
  } finally {
    clearTimeout(timer);
  }
}

/** GET with a PostgREST-style filter / select clause (e.g.
 *  "roster_name=eq.X&select=id,is_admin"). Returns the parsed JSON
 *  array. Used to re-sync local cache from the DB when realtime
 *  isn't enough — e.g. before performing a permission-change action
 *  whose decision depends on the target row's true current state. */
export async function restGet<T>(
  table: string,
  filter: string,
  timeoutMs = 12000,
): Promise<T[]> {
  const json = await restFetchJson(
    `${SUPABASE_URL}/rest/v1/${table}?${filter}`,
    {
      method: "GET",
      headers: authHeaders(),
    },
    timeoutMs,
  );
  return Array.isArray(json) ? (json as T[]) : [];
}

/** Paginated GET. PostgREST returns at most 1000 rows per response by
 *  default; for tables like calendar (~44k rows) we need to walk through
 *  pages using the Range header. This wraps that pattern so callers can
 *  treat large tables as a single .then(rows => …).
 *
 *  Bypasses the Supabase JS SDK entirely (same reason restGet does —
 *  the SDK's auth-token lock contends with the realtime channel and was
 *  observed crashing bootFromSupabase under concurrent tab activity).
 *
 *  `filter` is the PostgREST query string WITHOUT a leading `?` and
 *  WITHOUT a `select=` clause (we add `select` automatically). Pass
 *  e.g. `"active=eq.true"` or `""` for no filter.
 *
 *  Safety bound: hard-stops after 1000 pages (1M rows) so a misbehaving
 *  server can't trap us in an infinite loop. */
export async function restGetAll<T>(
  table: string,
  selectCols: string,
  filter = "",
  pageSize = 1000,
  timeoutMs = 30000,
): Promise<T[]> {
  const out: T[] = [];
  let from = 0;
  for (let i = 0; i < 1000; i++) {
    const to = from + pageSize - 1;
    const qs = `select=${encodeURIComponent(selectCols)}${filter ? `&${filter}` : ""}`;
    const headers = {
      ...authHeaders(),
      Range: `${from}-${to}`,
      "Range-Unit": "items",
    };
    const json = await restFetchJson(
      `${SUPABASE_URL}/rest/v1/${table}?${qs}`,
      { method: "GET", headers },
      timeoutMs,
    );
    const page = Array.isArray(json) ? (json as T[]) : [];
    out.push(...page);
    if (page.length < pageSize) break;
    from += pageSize;
  }
  return out;
}

/** Update the currently-signed-in user's password (or any other auth
 *  attribute that goes through Supabase Auth's `PUT /auth/v1/user`).
 *
 *  Why this isn't just `sb.auth.updateUser({ password })`:
 *  the SDK's auth helpers contend with the realtime channel for the
 *  same `navigator.locks` "sb-<ref>-auth-token" resource. Under any
 *  concurrency (which happens immediately after a magic-link sign-in
 *  because the realtime channel boots on auth state change), the SDK
 *  call can hang for the full 12-second timeout. Users see "Request
 *  timed out" and can't set their password — exactly the
 *  Daniele-Di-Fini bug.
 *
 *  Raw fetch reads the JWT straight from localStorage and shoots it
 *  past the SDK, same pattern as restPatch/restUpsert/restGet/restDelete. */
export async function restUpdatePassword(
  password: string,
  timeoutMs = 12000,
): Promise<void> {
  await restFetch(
    `${SUPABASE_URL}/auth/v1/user`,
    {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ password }),
    },
    timeoutMs,
  );
}

/** DELETE with a PostgREST-style filter string (e.g. "person=eq.X&day=in.(...)").
 *
 *  Safety: REFUSES to run with an empty / filter-less request. PostgREST
 *  interprets a bare DELETE as "delete every row" — a single bug in the
 *  caller that drops the filter would obliterate the table. The runtime
 *  check throws BEFORE any network call. */
export async function restDelete(
  table: string,
  filter: string,
  timeoutMs = 12000,
): Promise<void> {
  if (!filter || !filter.includes("=")) {
    throw new Error(
      `restDelete refusing to run on ${table}: empty / malformed filter (would delete every row).`,
    );
  }
  await restFetch(
    `${SUPABASE_URL}/rest/v1/${table}?${filter}`,
    {
      method: "DELETE",
      headers: { ...authHeaders(), Prefer: "return=minimal" },
    },
    timeoutMs,
  );
}
